// src/pages/Profile.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import {
  auth, getRiderProfile, listenToRiderProfile, createOrUpdateRider,
  saveFcmToken, requestNotificationPermission,
} from '../services/firebase';
import { useRider } from '../context/RiderContext';
import toast from 'react-hot-toast';
import { FEATURES } from '../config/features';
import { useLanguage } from '../i18n/LanguageContext';
import LangToggle from '../components/LangToggle';
import { openWhatsappSupport, callSupport, buildRiderHelpMessage } from '../utils/support';

const VEHICLE_TYPES = {
  rides: ['Bajaj Auto', 'TVS Auto', 'Piaggio Auto', 'Other Auto'],
  delivery: ['Bike', 'Bicycle', 'Scooter', 'Walking'],
  both: ['Bajaj Auto', 'TVS Auto', 'Bike', 'Scooter', 'Other'],
};

export default function Profile() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { rider, jobStats, riderRating } = useRider();
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Edit state
  const [editName, setEditName] = useState('');
  const [editVehicleType, setEditVehicleType] = useState('');
  const [editVehicleNumber, setEditVehicleNumber] = useState('');

  useEffect(() => {
    if (!rider) return;
    return listenToRiderProfile(rider.uid, (p) => {
      setProfile(p);
      if (!editing) {
        setEditName(p?.name || '');
        setEditVehicleType(p?.vehicle || '');
        setEditVehicleNumber(p?.vehicleNumber || '');
      }
    });
  }, [rider, editing]);

  const handleLogout = async () => {
    await signOut(auth);
    toast(t('profile.loggedOut'));
    navigate('/login');
  };

  const ratingDisplay = riderRating != null
    ? t('dashboard.ratingValue', { rating: riderRating })
    : t('dashboard.noRating');

  const handleSave = async () => {
    if (!editName.trim()) { toast.error(t('profile.nameRequired')); return; }
    if (!editVehicleNumber.trim()) { toast.error(t('profile.plateRequired')); return; }
    setSaving(true);
    try {
      await createOrUpdateRider(rider.uid, {
        name: editName.trim(),
        vehicle: editVehicleType,
        vehicleNumber: editVehicleNumber.trim().toUpperCase(),
      });
      const updated = await getRiderProfile(rider.uid);
      setProfile(updated);
      setEditing(false);
      toast.success(t('profile.updated'));
    } catch (e) {
      toast.error(t('profile.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const workTypeLabel = FEATURES.RIDES_ENABLED
    ? (profile?.workType === 'rides' ? '🛺 Auto Rides'
      : profile?.workType === 'delivery' ? '📦 Delivery'
      : profile?.workType === 'both' ? '🔄 Rides + Delivery'
      : '—')
    : t('profile.workDelivery');

  const vehicleWorkType = FEATURES.RIDES_ENABLED ? (profile?.workType || 'rides') : 'delivery';

  const handleEnableNotifications = async () => {
    const perm = await requestNotificationPermission();
    if (perm !== 'granted') {
      if (perm === 'denied') toast.error(t('notifications.blocked'));
      else toast.error(t('notifications.failed'));
      return;
    }
    const ok = await saveFcmToken(rider.uid);
    if (ok) toast.success(t('notifications.enabled'));
    else toast.error(t('notifications.failed'));
  };

  const menuItems = [
    {
      emoji: '💬',
      label: t('profile.helpWhatsapp'),
      sub: t('profile.helpWhatsappSub'),
      onClick: () => openWhatsappSupport(buildRiderHelpMessage(profile?.name)),
    },
    {
      emoji: '📞',
      label: t('profile.helpCall'),
      sub: t('profile.helpCallSub'),
      onClick: callSupport,
    },
    {
      emoji: '📄',
      label: t('profile.terms'),
      sub: t('profile.termsSub'),
    },
    {
      emoji: '🔔',
      label: t('profile.notifications'),
      sub: t('profile.notificationsSub'),
      onClick: handleEnableNotifications,
    },
  ];

  return (
    <div style={{ maxWidth: 420, margin: '0 auto', minHeight: '100vh', background: 'var(--black)', display: 'flex', flexDirection: 'column' }}>

      {/* Header */}
      <div style={{ background: 'linear-gradient(145deg, #0d1a10, #111318)', padding: '36px 24px 48px', textAlign: 'center', borderBottom: '1px solid var(--border)', position: 'relative' }}>
        {/* Edit button */}
        {!editing && (
          <div style={{ position: 'absolute', top: 16, right: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
            <LangToggle />
            <button onClick={() => setEditing(true)} style={{ background: 'rgba(0,214,143,0.1)', border: '1px solid rgba(0,214,143,0.3)', borderRadius: 10, padding: '6px 12px', color: 'var(--green)', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>
              {t('profile.edit')}
            </button>
          </div>
        )}

        <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'var(--green-glow)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, margin: '0 auto 12px', border: '2px solid var(--green)' }}>👨</div>

        {editing ? (
          <input
            value={editName}
            onChange={e => setEditName(e.target.value)}
            style={{ textAlign: 'center', fontSize: 18, fontWeight: 800, background: 'rgba(255,255,255,0.05)', border: '1.5px solid var(--green)', borderRadius: 10, padding: '8px 16px', width: '100%', maxWidth: 240, color: 'var(--text)', fontFamily: 'DM Sans, sans-serif' }}
            placeholder="Your name"
          />
        ) : (
          <div style={{ fontSize: 20, fontWeight: 800 }}>{profile?.name || t('profile.account')}</div>
        )}

        <div style={{ fontSize: 13, color: 'var(--gray)', marginTop: 4 }}>{profile?.phone}</div>
        <div style={{ display: 'inline-block', background: profile?.isApproved ? 'var(--green-glow)' : 'rgba(255,160,0,0.1)', color: profile?.isApproved ? 'var(--green)' : 'var(--orange)', fontSize: 11, fontWeight: 700, padding: '4px 14px', borderRadius: 20, marginTop: 8, border: `1px solid ${profile?.isApproved ? 'rgba(0,214,143,0.3)' : 'rgba(255,160,0,0.3)'}` }}>
          {profile?.isApproved ? t('profile.approvedPartner') : t('profile.pendingPartner')}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 16px 100px', marginTop: -20 }}>

        {/* Vehicle card */}
        {profile && (
          <div className="card" style={{ padding: 16, marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 12, color: 'var(--gray)' }}>{t('profile.yourVehicle')}</div>

            {editing ? (
              <>
                {/* Vehicle type chips */}
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, color: 'var(--gray)', marginBottom: 8, fontWeight: 700 }}>{t('profile.vehicleType')}</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {VEHICLE_TYPES[vehicleWorkType]?.map(v => (
                      <div key={v} onClick={() => setEditVehicleType(v)}
                        style={{ padding: '7px 14px', borderRadius: 10, border: `1.5px solid ${editVehicleType === v ? 'var(--green)' : 'var(--border)'}`, background: editVehicleType === v ? 'rgba(0,214,143,0.08)' : 'var(--dark)', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: editVehicleType === v ? 'var(--green)' : 'var(--text)' }}>
                        {v}
                      </div>
                    ))}
                  </div>
                </div>
                {/* Vehicle number */}
                <div>
                  <div style={{ fontSize: 11, color: 'var(--gray)', marginBottom: 8, fontWeight: 700 }}>{t('profile.numberPlate')}</div>
                  <input
                    value={editVehicleNumber}
                    onChange={e => setEditVehicleNumber(e.target.value.toUpperCase())}
                    placeholder="AP39AB1234"
                    style={{ fontSize: 15, fontWeight: 700, letterSpacing: 2 }}
                  />
                </div>
              </>
            ) : (
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <div style={{ fontSize: 36 }}>📦</div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{profile.vehicle}</div>
                  <div style={{ background: 'var(--border)', color: 'var(--text)', fontSize: 13, fontWeight: 800, padding: '4px 12px', borderRadius: 8, marginTop: 6, display: 'inline-block', letterSpacing: 2, fontFamily: 'DM Mono, monospace' }}>{profile.vehicleNumber}</div>
                  <div style={{ fontSize: 12, color: 'var(--gray)', marginTop: 4 }}>{workTypeLabel}</div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Save / Cancel buttons when editing */}
        {editing && (
          <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
            <button className="btn btn-dark" style={{ flex: 1 }} onClick={() => {
              setEditing(false);
              setEditName(profile?.name || '');
              setEditVehicleType(profile?.vehicle || '');
              setEditVehicleNumber(profile?.vehicleNumber || '');
            }}>{t('common.cancel')}</button>
            <button className="btn btn-green" style={{ flex: 2 }} onClick={handleSave} disabled={saving}>
              {saving ? <span className="spinner" /> : t('profile.saveChanges')}
            </button>
          </div>
        )}

        {/* Stats */}
        <div className="card" style={{ padding: 16, marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 10, color: 'var(--gray)' }}>{t('profile.yourStats')}</div>
          {[
            { label: t('profile.totalJobs'), value: jobStats.totalTrips },
            { label: t('profile.rating'), value: ratingDisplay },
            { label: 'Work Type', value: workTypeLabel, hide: !FEATURES.RIDES_ENABLED },
            { label: t('profile.memberSince'), value: profile?.createdAt ? new Date(profile.createdAt).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }) : '—' },
          ].filter(r => !r.hide).map(r => (
            <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontSize: 13, borderBottom: '1px solid var(--border)' }}>
              <span style={{ color: 'var(--gray)' }}>{r.label}</span>
              <span style={{ fontWeight: 700 }}>{r.value}</span>
            </div>
          ))}
        </div>

        {/* Menu */}
        <div className="card" style={{ marginBottom: 12, overflow: 'hidden' }}>
          {menuItems.map((item, i, arr) => (
            <div
              key={item.label}
              role={item.onClick ? 'button' : undefined}
              tabIndex={item.onClick ? 0 : undefined}
              onClick={item.onClick}
              onKeyDown={item.onClick ? (e) => { if (e.key === 'Enter') item.onClick(); } : undefined}
              style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 14, borderBottom: i < arr.length - 1 ? '1px solid var(--border)' : 'none', cursor: item.onClick ? 'pointer' : 'default' }}
            >
              <div style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--dark)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>{item.emoji}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{item.label}</div>
                <div style={{ fontSize: 12, color: 'var(--gray)' }}>{item.sub}</div>
              </div>
              <span style={{ color: 'var(--gray)' }}>›</span>
            </div>
          ))}
        </div>

        <button onClick={handleLogout} style={{ width: '100%', padding: 14, border: '1px solid var(--red)', borderRadius: 14, background: 'transparent', color: 'var(--red)', fontFamily: 'DM Sans, sans-serif', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
          {t('profile.logout')}
        </button>

        <div style={{ textAlign: 'center', padding: '20px 0', fontSize: 11, color: 'var(--gray)' }}>
          {t('profile.version')}
        </div>
      </div>

      <div className="bottom-nav">
        {[
          { label: t('nav.home'), emoji: '🏠', path: '/' },
          { label: t('nav.history'), emoji: '📋', path: '/history' },
          { label: t('nav.profile'), emoji: '👤', path: '/profile', active: true },
        ].map(n => (
          <button key={n.label} className={`nav-item ${n.active ? 'active' : ''}`} onClick={() => navigate(n.path)}>
            <div className="nav-icon">{n.emoji}</div>
            <div className="nav-label">{n.label}</div>
          </button>
        ))}
      </div>
    </div>
  );
}